import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { isFileNameValid, LineOfBusiness, ProductNames } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService, ToastMessageOptions } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { timer } from "rxjs";
import { DocumentSourceType } from '@core/enumeration';
import { AuthorizationPacDTO, BaseFile, Product, ServiceLineSummaryDTO, UploadError, UploadFile } from "@shared/model";
import { ProductService } from '@core/service/product.service';
import { DocumentUtilities } from "@shared/utility";
import { DateTimeModalComponent } from "../../documents-upload/date-time-modal/date-time-modal.component";
import { DocumentTypes, DocumentTypes_UF, DocumentTypesSuggestedChanges, NomncRescind } from "../document-pacim-dropdown.constant";

@Component({
    selector: 'upload-files-pacim',
    templateUrl: './upload-files-pacim.component.html',
    styleUrls: ['./upload-files-pacim.component.scss'],
    standalone: false
})
export class UploadFilesPACIMComponent implements OnInit, OnChanges {
  @Input() reviewConfirmationPopupNeeded = false;
  @Input() isExternal = true;
  @Input() disableComponent = false;
  @Input() dateTimeFaxReceivedUTC: string | null = null;
  @Input() isContinueRequest = false;
  @Input() isProviderDashboard = false;
  @Input() isDocumentDashboard = false;
  @Input() docTypesPlaceholder = '';
  @Input() productData?: Product;
  @Input() authorizationNumber?: string;
  @Input() isSuggestedChangesProcess?: string;
  @Input() serviceLinesSummary?: ServiceLineSummaryDTO[];
  @Input() authDocuments?: BaseFile[];
  @Input() programState!: string;
  @Input() isACMPApprovalLetterOverdue = false;
  @Input() shouldShowCSPopup = false;
  @Input() crsMessage: ToastMessageOptions = {};
  @Input() lineOfBusiness?: string;

  private _authorizationSearch!: AuthorizationPacDTO;
  @Input() get authorizationSearch(): AuthorizationPacDTO {
    return this._authorizationSearch;
  };
  set authorizationSearch(value: AuthorizationPacDTO) {
    this._authorizationSearch = value;
    this.isPacCommercial = this.lineOfBusiness === LineOfBusiness.Commercial;

    const facilityType = value?.facilityType.description;

    this.hasLevelAdjustment = this.isPacCommercial
      ? facilityType !== 'IRF'
      : facilityType === this.snf;
  }
  @Input() autoUpload!: boolean;
  @Input() tenantId!: string;
  @Input() tenantName!: string;
  @Input() subscriberNumber?: string;
  // @Input() set documents(value: DocumentsPACIM[]) {
  //   this.uploadedFiles = value ? [...value] : [];
  // }
  @Input() set documents(value: UploadFile[]) {
    this.uploadedFiles = value ? [...value] : [];
    this.checkLimit();
    this.checkForNomncRescind();
  }
  get documents() {
    return this.uploadedFiles;
  }

  @Input() nomncRescindSelected = false;
  //@Output() uploadedNewFile = new EventEmitter<DocumentsPACIM[]>();
  @Output() uploadedNewFile = new EventEmitter<UploadFile[]>();
  @Output() selectedFiles = new EventEmitter<UploadFile[]>();
  @Output() createContinuedStay = new EventEmitter<void>();

  descriptionForm!: FormGroup;
  isLimitFour = false;
  display = false;
  showButton = false;
  filesSizeSum = 0;
  fileTypeErrors = false;
  fileSizeErrors = false;
  fileNameInvalidErrors = false;
  selectedDocumentTypes: string[] = [];
  //uploadedFiles: DocumentsPACIM[] = []; // not used in PACIM Api Files/UploadDocuments
  uploadedFiles: UploadFile[] = []; // map correct Interface TODO -- used endpoint
  readonly allowedExtensions: string[] = ['.jpg', '.jpeg', '.png', '.pdf'];
  readonly maxFileSize = 20 * 1024 * 1024; // 20Mb
  readonly maxContinuedRequestFileNameLength = 130;  // Names which length are greater than 130 should be cropped on continueRequest document tab
  IsNotReadyToUpload = false;
  isUploading = false;
  UploadMessageExternal: ToastMessageOptions = null;
  UploadLengthExcessMessage: ToastMessageOptions = null;
  docTypesExternal: any[] = [];

  levelAdjustmentsSelected = false;
  isPacCommercial = false;
  hasLevelAdjustment = false;
  productId!: string;

  private readonly denc = 'DENC';
  private readonly nomnc = 'NOMNC';
  private readonly levelAdjustment = 'Level Adjustment';
  private readonly snf = 'SNF';
  private readonly providerAppeal = 'Provider Appeal';
  private readonly memberAppeal = 'Member Appeal';

  get documentTypesList() {
    if (this.isContinueRequest || this.isProviderDashboard) {
      if (this.nomncRescindSelected) {
        return NomncRescind;
      } else if (this.isSuggestedChangesProcess) {
        return DocumentTypesSuggestedChanges;
      } else {
        return DocumentTypes;
      }
    }

    let documentTypes_UF = DocumentTypes_UF;

    if (this.isPacCommercial) {
      documentTypes_UF = documentTypes_UF.filter(
        (x) =>
        x.value !== this.denc &&
        x.value !== this.nomnc)
    }

    if (this.isACMPApprovalLetterOverdue) {
      documentTypes_UF = documentTypes_UF
        .filter(docType => docType.value !== this.levelAdjustment);
    }

    if (this.isExternal) {
      this.docTypesExternal = documentTypes_UF.filter(
        (x) =>
          x.value !== this.denc &&
          x.value !== this.memberAppeal &&
          x.value !== this.providerAppeal
      );

      if (!this.hasLevelAdjustment) {
        this.docTypesExternal = documentTypes_UF.filter(
          (x) =>
            x.value !== this.levelAdjustment
        );
      }

      return this.docTypesExternal;
    } else if (!this.hasLevelAdjustment) {
      const documentTypesForNotSNF = documentTypes_UF.filter(
        (x) =>
          x.value !== this.levelAdjustment
      );

      return documentTypesForNotSNF;
    } else return documentTypes_UF;
  }

  get showSaveButton(): boolean {
    return !!this.uploadedFiles?.length && !this.autoUpload && !this.isContinueRequest && !this.isSuggestedChangesProcess;
  }

  get warningPopupHeader(): string {
    if (this.fileNameInvalidErrors) {
      return 'Invalid file name';
    } else if (this.fileTypeErrors) {
      return 'Incorrect file format';
    } else {
      return `File exceeds ${this.maxFileSize / (1024 * 1024)}Mb`;
    }
  }

  constructor(
    private fb: FormBuilder,
    private ref: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private productService: ProductService,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { authorizationSearch } = changes;
    if (!authorizationSearch?.firstChange && (authorizationSearch?.currentValue !== authorizationSearch?.previousValue)) {
      this.reset();
    }
  }

  ngOnInit() {
    this.productService
      .getProductByShortName(ProductNames.PACIM)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pacProduct => {
        this.productId = pacProduct.productId;
      });
    this.buildForm();
    this.showMessage();

    if (this.isSuggestedChangesProcess) {
      this.getDocumentTypes();
      this.showUploadButton(this.selectedDocumentTypes);
    }

    this.checkForNomncRescind();
  }

  deleteItem(event: number) {
    this.uploadedFiles.splice(event, 1);

    if(!this.isSuggestedChangesProcess) {
      this.resetUploadFields();
    }

    this.checkLimit();
    this.sumAllFileSize();
    this.uploadedNewFile.emit(this.uploadedFiles);
  }

  showUploadButton(documentTypes: string[]) {
    if (this.isDocumentTypesOther) {
      const fileDescription = (this.descriptionForm.get(
        'fileDescription'
      )?.value as string)?.trim();

      this.showButton = !!fileDescription?.length;
    } else
    {
      this.showButton = documentTypes.length > 0;
    }

    this.ref.markForCheck();
  }

  onError(errors: UploadError[]) {
    this.fileTypeErrors = errors.some((e) => e.type === 'accept');
    this.fileSizeErrors = errors.some((e) => e.type === 'fileSize');
    this.fileNameInvalidErrors = errors.some((e) => e.type === 'fileName');
    this.showDialog();
    this.hideButton();

    if (!this.isSuggestedChangesProcess) {
      this.resetUploadFields();
    }

    this.checkForNomncRescind();
  }

  onBack() {
    this.display = false;

    if (this.isSuggestedChangesProcess) {
      this.showUploadButton(this.selectedDocumentTypes);
    }
  }

  addFile(files: File[]) {
    switch (true) {
      case this.isExternal:
        this.addSelected(files);
        break;
      case Boolean(this.dateTimeFaxReceivedUTC):
        this.addSelected(files, this.dateTimeFaxReceivedUTC as string);
        break;
      default:
        // eslint-disable-next-line no-case-declarations
        const ref = this.dialogService.open(DateTimeModalComponent, {
          header: 'Select the Date and Time Request Received',
          width: '500px',
          modal: true,
          closable: true,
        });

        ref.onClose.pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((dateTimeFaxReceived: string) => {
            if (dateTimeFaxReceived) {
              this.addSelected(files, dateTimeFaxReceived);
            } else {
              this.hideButton();
              this.resetUploadFields();
            }
            this.ref.markForCheck();
          });
    }
    this.ref.markForCheck();
  }

  async addSelected(files: FileList | File[], dateTimeFaxReceived?: string) {
    const file: File = files[0];
    const indexNumber = this.uploadedFiles ? this.uploadedFiles.length : 0;

    if (this.maxFileSize && this.filesSizeSum + file.size > this.maxFileSize) {
      const error: UploadError[] = [{ type: 'fileSize', name: file.name }];

      this.onError(error);
    } else if (!isFileNameValid(file.name).valid) {
          const error: UploadError[] = [{type: 'fileName', name: file.name}];
          this.onError(error);
    } else {
      if (this.shouldShowExcessMessage(file)) {
        this.showExcessMessage();
      }

      this.resetValidationErrors();

      if (this.levelAdjustmentsSelected) {
        this.getDocumentTypes();
      }

      try {
        const fileBase64: string = (await this.readFileAsync(file)) as string;
        const fileContent = fileBase64.split(',')[1];
        const allowedFileNameLength = this.isContinueRequest || this.isProviderDashboard || this.isDocumentDashboard ? this.maxContinuedRequestFileNameLength : undefined;
        const description = (this.descriptionForm.get(
          'fileDescription'
        )?.value as string)?.trim();

        this.uploadedFiles[indexNumber] = {
          fileMetaData: {
            size: file.size,
            type: file.type,
          },
          authorizationId: this.authorizationSearch?.authorizationByProductId,
          authorizationNumber: this.authorizationSearch?.authorizationProduct?.authorizationNumber || this.authorizationNumber,
          tenantId: this.tenantId,
          tenantName: this.tenantName,
          documentSource: 'Upload',
          isFromCreateAuth: false,
          fileContent,
          documentTypes: this.selectedDocumentTypes,
          dateTimeFaxReceived: dateTimeFaxReceived || new Date().toUTCString(),
          description,
          documentFileName: DocumentUtilities.addFilenameTimestamp(file.name, allowedFileNameLength),
          productId: this.isProviderDashboard ? this.productData?.productId : this.productId
        };

        // if (this.reviewConfirmationPopupNeeded && this.isExternal) {
        //   this.uploadedFiles[indexNumber].isNeedCheckStaff =
        //     await this.confirmNeedCheckStaff();
        // }

        this.hideButton();
        this.resetUploadFields();
        this.checkLimit();
        this.sumAllFileSize();

        this.uploadedNewFile.emit(this.uploadedFiles);

        if (this.isSuggestedChangesProcess) {
          this.getDocumentTypes();
          setTimeout(() => this.showUploadButton(this.selectedDocumentTypes), 250);
        }
      } catch (error) {
        console.log("Upload file error: ", error);
      }
    }

    this.ref.markForCheck();
  }

  // Upload new document
  uploadDocuments() {
    this.IsNotReadyToUpload = true;
    if (this.uploadedFiles.length > 0) {
      this.isUploading = true;
      const uploadFileDTOs: UploadFile[] = this.uploadedFiles.map((f) => {
        const { fileMetaData, ...newFile } = f;
        return newFile;
      });
      this.selectedFiles.emit(uploadFileDTOs);
      this.filesSizeSum = 0;
      this.uploadedFiles = [];
      this.isLimitFour = false;
      this.hideButton();
      this.isUploading = false;
      this.levelAdjustmentsSelected = false;
    }
  }

  get isDocumentTypesOther() {
    return (
      this.selectedDocumentTypes.length === 1 &&
      this.selectedDocumentTypes.indexOf('Other') > -1
    );
  }

  private confirmNeedCheckStaff(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.confirmationService.confirm({
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  resetValidationErrors() {
    this.fileTypeErrors = false;
    this.fileSizeErrors = false;
    this.fileNameInvalidErrors = false;
  }

  showDialog() {
    this.display = true;
  }

  checkLimit() {
    // Note: EHR documents are not counted towards the limit of
    // 4 documents that can be uploaded
    this.isLimitFour = this.uploadedFiles.filter(
      (file) => file.documentSource != DocumentSourceType.EHR
    ).length >= 4;
  }

  onSelectlevelAdjustment(): void {
    this.levelAdjustmentsSelected = true;
  }

  private getDocumentTypes(): void {
    if (!this.selectedDocumentTypes.includes('Level Adjustment')) {
      this.selectedDocumentTypes.push('Level Adjustment');
    }
  }

  private resetUploadFields() {
    this.descriptionForm.reset();
    this.selectedDocumentTypes = [];
  }

  private shouldShowExcessMessage(file: File): boolean {
    return (this.isContinueRequest || this.isProviderDashboard || this.isDocumentDashboard) && file.name.length > this.maxContinuedRequestFileNameLength
  }

  private showExcessMessage(): void {
    const detail = `The selected file’s name exceeds the ${this.maxContinuedRequestFileNameLength} characters limit. The file name will have to be cropped to the appropriate length.`;

    this.UploadLengthExcessMessage = { severity: 'warn', summary: 'Warning:', detail };

    timer(10000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.UploadLengthExcessMessage = null;

      this.ref.markForCheck();
    });
  }

  private sumAllFileSize() {
    // Note: EHR documents are not counted towards the total file size
    // for the purpose of validating against the max file size limit
    this.filesSizeSum = this.uploadedFiles.filter(
      (file) => file.documentSource != DocumentSourceType.EHR
    ).reduce(
      (sum, { fileMetaData }) => {
        let size = 0;
        if (fileMetaData?.size) {
          size = fileMetaData.size;
        }
        return sum + size
      },
      0
    );
  }

  private hideButton(): void {
    this.showButton = false;
  }

  private readFileAsync(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reader: any = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private buildForm(): void {
    this.descriptionForm = this.fb.group({
      fileDescription: ['', Validators.maxLength(25)]
    });
  }

  private showMessage(): void {
    if (!this.isContinueRequest && !this.isProviderDashboard) {
      const detail = `You will be uploading additional files to Authorization ${this.authorizationSearch.authorizationProduct.authorizationNumber}. Uploading additional
      information does NOT constitute a new request.To request further authorization, go to Services tab and click the button to complete a request.`;

      this.UploadMessageExternal = { summary: 'Alert:', detail };
    }
  }

  private reset(): void {
    this.descriptionForm?.reset();
    this.showMessage();
    this.selectedDocumentTypes = [];
    this.levelAdjustmentsSelected = false;
    this.uploadedFiles = [];
  }

  private checkForNomncRescind(): void  {
    if (this.nomncRescindSelected && !this.isLimitFour && this.filesSizeSum <= this.maxFileSize) {
      this.selectedDocumentTypes = [NomncRescind[0].value];
    }

    this.showUploadButton(this.selectedDocumentTypes);
  }
}
